import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { KanbanSquare, Plus, ArrowLeft, GripVertical, MessageSquare, Mail, Trash2, Send, UserPlus, Search } from "lucide-react";
import { api, errorDetail } from "@/lib/api";
import { PageHeader, EmptyState, Pill } from "@/components/common";
import { Avatar } from "@/components/CandidateCard";
import CandidatePanel from "@/components/CandidatePanel";
import { AddCandidateDialog } from "@/components/AddCandidateDialog";
import { PoolPickerDialog } from "@/components/PoolPickerDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { tk } from "@/i18n";
import { fmtDateTime, fmtShortDate } from "@/i18n/format";

export const PROJECT_STATUS = { open: tk("Open"), on_hold: tk("On hold"), closed: tk("Closed") };
export const STAGES = [tk("Identified"), tk("Shortlisted"), tk("Contacted"), tk("Replied"), tk("Interview"), tk("Hired")];
const MAIL_STATUS = { awaiting_reply: tk("awaiting reply"), replied: tk("replied") };

const MAIL_TONE = { awaiting_reply: "amber", replied: "green" };

function TeamNotes({ p, onAdded }) {
  const { t } = useI18n();
  const [body, setBody] = useState("");
  const [cid, setCid] = useState("");
  const add = async () => {
    try { await api.post(`/projects/${p.id}/notes`, { body, candidate_id: cid || null }); setBody(""); onAdded(); } catch (e) { toast.error(errorDetail(e)); }
  };
  return (
    <div className="space-y-2 mb-3">
      <Textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("Share an update with your team… Mention a teammate with @Full Name")} data-testid="project-note-input" />
      <div className="flex gap-2">
        <select className="h-8 rounded-md border bg-background px-2 text-xs" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="project-note-candidate">
          <option value="">{t("About the whole project")}</option>{p.entries.map((e) => <option key={e.id} value={e.candidate.id}>{e.candidate.full_name}</option>)}</select>
        <Button size="sm" variant="outline" disabled={!body.trim()} onClick={add} data-testid="project-note-submit"><Send className="w-4 h-4" />{t("Post")}</Button>
      </div>
    </div>
  );
}

export function Projects() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState("");
  const load = () => api.get("/projects").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => { try { await api.post("/projects", { title }); setTitle(""); load(); } catch (e) { toast.error(errorDetail(e)); } };
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader eyebrow={t("CRM")} title={t("Recruitment projects")} actions={<><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("e.g. Senior Robotics Engineer — Lyon")} className="h-9 w-72" data-testid="new-project-input" /><Button size="sm" onClick={create} disabled={!title.trim()} data-testid="create-project-button"><Plus className="w-4 h-4" />{t("New project")}</Button></>} />
      {!rows.length && <EmptyState icon={KanbanSquare} title={t("No projects yet")} body={t("Create a project, then add candidates from Talent Search.")} />}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((p) => (
          <Link key={p.id} to={`/app/projects/${p.id}`} className="card-surface p-5 hover:border-primary/40 transition-colors" data-testid={`project-card-${p.id}`}>
            <div className="flex justify-between"><Pill tone={p.status === "open" ? "green" : "default"}>{t(PROJECT_STATUS[p.status] || p.status)}</Pill><span className="text-xs text-muted-foreground">{fmtShortDate(p.created_at)}</span></div>
            <div className="font-semibold mt-3">{p.title}</div>
            <div className="text-xs text-muted-foreground mt-1">{t("{{count}} candidates", { count: p.candidate_count })} · {t("{{count}} stages", { count: p.stages.length })}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ProjectBoard() {
  const { t } = useI18n();
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [drag, setDrag] = useState(null);
  const [sel, setSel] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const load = () => api.get(`/projects/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(errorDetail(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  const move = async (entry, stage) => {
    if (!entry || entry.stage === stage) return;
    setP({ ...p, entries: p.entries.map((e) => e.id === entry.id ? { ...e, stage } : e) });
    try { await api.patch(`/pipelines/${entry.id}`, { stage, position: 0 }); } catch (e) { toast.error(errorDetail(e)); load(); }
  };
  const remove = async (entry) => {
    try { await api.delete(`/pipelines/${entry.id}`); setP({ ...p, entries: p.entries.filter((e) => e.id !== entry.id) }); } catch (e) { toast.error(errorDetail(e)); }
  };
  const setStatus = async (status) => {
    try { await api.patch(`/projects/${p.id}`, { title: p.title, description: p.description, status }); setP({ ...p, status }); } catch (e) { toast.error(errorDetail(e)); }
  };
  if (!p) return <div className="p-6"><div className="h-40 rounded-xl bg-muted shimmer" /></div>;
  return (
    <div className="p-6">
      <Link to="/app/projects" className="text-xs text-muted-foreground flex items-center gap-1 mb-3"><ArrowLeft className="w-3 h-3" />{t("Projects")}</Link>
      <PageHeader eyebrow={t("Pipeline")} title={p.title} desc={t("{{count}} candidates · drag cards between stages", { count: p.entries.length })}
        actions={<><Button size="sm" variant="outline" onClick={() => setPickOpen(true)} data-testid="project-add-from-pool"><Search className="w-4 h-4" />{t("Add from the talent pool")}</Button>
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="project-add-candidate"><UserPlus className="w-4 h-4" />{t("Add a candidate")}</Button>
          <select className="h-9 rounded-md border bg-background px-2 text-sm" value={p.status} onChange={(e) => setStatus(e.target.value)} data-testid="project-status-select">
          {Object.entries(PROJECT_STATUS).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}</select></>} />
      <AddCandidateDialog open={addOpen} onOpenChange={setAddOpen} projectId={p.id} onCreated={load} onOpenExisting={(d) => setSel({ id: d.id })} />
      <PoolPickerDialog project={p} open={pickOpen} onOpenChange={setPickOpen} onAdded={load} />
      <div className="flex gap-3 overflow-x-auto pb-4" data-testid="pipeline-board">
        {p.stages.map((s) => {
          const items = p.entries.filter((e) => e.stage === s);
          return (
            <div key={s} onDragOver={(e) => e.preventDefault()} onDrop={() => move(drag, s)} data-testid={`stage-${s}`}
              className="w-64 shrink-0 rounded-xl bg-muted/60 p-2 min-h-[300px]">
              <div className="flex justify-between px-2 py-1.5"><span className="text-xs font-semibold">{t(s)}</span><span className="text-xs text-muted-foreground font-mono">{items.length}</span></div>
              <div className="space-y-2">
                {items.map((e) => (
                  <div key={e.id} draggable onDragStart={() => setDrag(e)} onClick={() => setSel(e.candidate)} data-testid={`pipeline-card-${e.id}`}
                    className="card-surface p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow flex gap-2">
                    <GripVertical className="w-3.5 h-3.5 text-muted-foreground mt-1" />
                    <Avatar name={e.candidate.full_name} size={8} />
                    <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{e.candidate.full_name}</div><div className="text-xs text-muted-foreground truncate">{e.candidate.current_title}</div>
                      {e.email_status && <div className="mt-1"><Pill tone={MAIL_TONE[e.email_status]} testid={`pipeline-email-status-${e.id}`}><Mail className="w-3 h-3" />{t(MAIL_STATUS[e.email_status] || e.email_status.replace("_", " "))}</Pill></div>}</div>
                    <button onClick={(ev) => { ev.stopPropagation(); remove(e); }} className="self-start p-0.5 text-muted-foreground hover:text-red-600" data-testid={`remove-entry-${e.id}`} aria-label={t("Remove from project")}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="card-surface p-5 mt-4 max-w-3xl">
        <div className="font-semibold text-sm flex items-center gap-1.5 mb-2"><MessageSquare className="w-4 h-4" />{t("Team notes & activity")}</div>
        <TeamNotes p={p} onAdded={load} />
        {p.activity.map((n) => <div key={n.id} className="text-xs py-1.5 border-t" data-testid={`project-note-${n.id}`}><b>{n.author}</b>{n.candidate_name ? <span className="text-muted-foreground"> {t("on {{name}}", { name: n.candidate_name })}</span> : ""}: {n.body} <span className="text-muted-foreground">· {fmtDateTime(n.created_at)}</span></div>)}
        {p.entries.flatMap((e) => e.history.map((h) => ({ ...h, name: e.candidate.full_name }))).sort((a, b) => b.at.localeCompare(a.at)).slice(0, 15)
          .map((h, i) => <div key={i} className="text-xs py-1.5 border-t">{h.from ? t("{{by}} moved {{name}} from {{from}} to {{to}}", { by: h.by, name: h.name, from: t(h.from), to: t(h.stage) }) : t("{{by}} moved {{name}} to {{to}}", { by: h.by, name: h.name, to: t(h.stage) })} · {fmtDateTime(h.at)}</div>)}
      </div>
      {sel && <aside className="fixed right-0 top-0 h-screen w-full sm:w-[420px] bg-background border-l z-50 overflow-y-auto shadow-2xl" data-testid="pipeline-candidate-panel"><CandidatePanel id={sel.id} onClose={() => { setSel(null); }} /></aside>}
    </div>
  );
}
